/**
 * IdP provider presets — pre-fills known configuration for common identity providers.
 * The admin only needs to provide their tenant/domain-specific values + credentials.
 */

import { AttributeMapping } from './AttributeMappingsStep';

export type IdpProvider = 'entra-id' | 'google' | 'okta' | 'custom-oidc' | 'custom-saml';

export interface IdpPreset {
  readonly id: IdpProvider;
  readonly name: string;
  readonly description: string;
  readonly protocol: 'oidc' | 'saml';
  /** If set, this is the fixed metadata URL (no template needed) */
  readonly metadataUrl?: string;
  /** If set, this is a URL template with a placeholder like {tenant-id} or {domain} */
  readonly metadataUrlTemplate?: string;
  /** The placeholder label for the template (e.g., "Tenant ID", "Okta Domain") */
  readonly templatePlaceholderLabel?: string;
  /** Helper text for the template placeholder */
  readonly templatePlaceholderHint?: string;
  /** Default attribute mappings for this provider */
  readonly defaultMappings: AttributeMapping[];
  /** Whether client secret is required (Google uses it, but some flows don't) */
  readonly requiresClientSecret: boolean;
}

export const IDP_PRESETS: readonly IdpPreset[] = [
  {
    id: 'entra-id',
    name: 'Microsoft Entra ID',
    description: 'Azure Active Directory / Microsoft 365',
    protocol: 'oidc',
    metadataUrlTemplate: 'https://login.microsoftonline.com/{tenant-id}/v2.0/.well-known/openid-configuration',
    templatePlaceholderLabel: 'Tenant ID',
    templatePlaceholderHint: 'Your Azure AD tenant ID (a UUID like 12345678-abcd-1234-abcd-123456789abc) or domain (e.g., contoso.onmicrosoft.com)',
    defaultMappings: [
      { sourceClaim: 'email', targetProperty: 'email' },
      { sourceClaim: 'preferred_username', targetProperty: 'displayName' },
      { sourceClaim: 'groups', targetProperty: 'groups' },
    ],
    requiresClientSecret: true,
  },
  {
    id: 'google',
    name: 'Google Workspace',
    description: 'Google Cloud Identity / Workspace',
    protocol: 'oidc',
    metadataUrl: 'https://accounts.google.com/.well-known/openid-configuration',
    defaultMappings: [
      { sourceClaim: 'email', targetProperty: 'email' },
      { sourceClaim: 'name', targetProperty: 'displayName' },
    ],
    requiresClientSecret: true,
  },
  {
    id: 'okta',
    name: 'Okta',
    description: 'Okta Identity Platform',
    protocol: 'oidc',
    metadataUrlTemplate: 'https://{domain}.okta.com/.well-known/openid-configuration',
    templatePlaceholderLabel: 'Okta Domain',
    templatePlaceholderHint: 'Your Okta subdomain (e.g., "my-company" from my-company.okta.com)',
    defaultMappings: [
      { sourceClaim: 'email', targetProperty: 'email' },
      { sourceClaim: 'preferred_username', targetProperty: 'displayName' },
      { sourceClaim: 'groups', targetProperty: 'groups' },
    ],
    requiresClientSecret: true,
  },
  {
    id: 'custom-oidc',
    name: 'Custom OIDC',
    description: 'Any OpenID Connect provider',
    protocol: 'oidc',
    defaultMappings: [],
    requiresClientSecret: true,
  },
  {
    id: 'custom-saml',
    name: 'Custom SAML',
    description: 'Any SAML 2.0 provider',
    protocol: 'saml',
    defaultMappings: [],
    requiresClientSecret: true,
  },
];

/**
 * Resolves a metadata URL from a preset and a template value.
 */
export function resolveMetadataUrl(preset: IdpPreset, templateValue: string): string {
  if (preset.metadataUrl) return preset.metadataUrl;
  if (preset.metadataUrlTemplate && templateValue) {
    // Replace any placeholder pattern like {tenant-id} or {domain}
    return preset.metadataUrlTemplate.replace(/\{[^}]+\}/, templateValue.trim());
  }
  return '';
}
